import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProfileService from "service/ProfileService";
import { updateProfile } from "action/AuthAction";

const ManageProfile: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profileService = ProfileService();
  
  const { profile, token } = useSelector((state: any) => state.auth);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tagLine, setTagLine] = useState("");
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [originalName, setOriginalName] = useState("");
  const [originalTagLine, setOriginalTagLine] = useState("");
  const [originalPic, setOriginalPic] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      setError(null);
      setSuccess(null);
      
      const authToken = token || profile?.data?.token;
      if (!authToken) {
        setError("Token tidak ditemukan. Silakan login kembali.");
        setIsFetching(false);
        return;
      }

      try {
        const res = await profileService.getProfile(authToken);
        const data = res?.data;

        if (!data) throw new Error("Data profil tidak ditemukan");

        setName(data.name || "");
        setEmail(data.email || "");
        setTagLine(data.tagLine || "");
        setRoles(data.roles || []);
        setPreview(data.profilePicture || null);
        setOriginalName(data.name || "");
        setOriginalTagLine(data.tagLine || "");
        setOriginalPic(data.profilePicture || null);
      } catch (err: any) {
        setError(err?.message || "Gagal memuat profil");
      } finally {
        setIsFetching(false);
      }
    };

    fetchProfile();
  }, [profile, token]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePic(file);
      setPreview(URL.createObjectURL(file));
      setError(null);
      setSuccess(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError("Nama tidak boleh kosong");
      return;
    }

    const authToken = token || profile?.data?.token;
    if (!authToken) {
      setError("Token tidak ditemukan. Silakan login kembali.");
      return;
    }

    setLoading(true);
    try {
      const res = await profileService.updateProfile(
        authToken, 
        name.trim(), 
        tagLine,
        profilePic
      );
      const updated = res.data;

      if (!updated) throw new Error("Data kosong");

      dispatch(updateProfile({
        name: updated.name,
        email: updated.email,
        tagLine: updated.tagLine,
        roles: updated.roles,
        profilePicture: updated.profilePicture,
        ...(profile?.data ? {
          id: profile.data.id,
          token: profile.data.token,
        } : {})
      }));

      setSuccess("Profil berhasil diperbarui!");
      setOriginalName(updated.name || "");
      setOriginalTagLine(updated.tagLine || "");
      setOriginalPic(updated.profilePicture ?? null);
      setProfilePic(null);
      
      if (updated.profilePicture) {
        setPreview(updated.profilePicture);
      }
    } catch (err: any) {
      setError(err?.message || "Gagal memperbarui profil");
    } finally {
      setLoading(false);
    }
  };

  const isChanged = name.trim() !== originalName || tagLine !== originalTagLine || profilePic !== null;

  if (isFetching) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Manage Profile</h5>
          </div>
        </div>

        <div className="col-12">
          <div
            className="p-4"
            style={{ background: "white", borderRadius: "6px" }}
          >
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <label className="form-label fw-bold">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setError(null);
                        setSuccess(null);
                      }}
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      disabled
                      style={{ backgroundColor: "#f2f2f2" }}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">Tag Line</label>
                    <input
                      type="text"
                      className="form-control"
                      value={tagLine}
                      onChange={(e) => {
                        setTagLine(e.target.value)
                        setError(null);
                        setSuccess(null);
                      }}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label fw-bold">Roles</label>
                    <ul className="mb-0">
                      {roles.length > 0 ? (
                        roles.map((r) => <li key={r.id}>{r.name}</li>)
                      ) : (
                        <li>No roles assigned</li>
                      )}
                    </ul>
                  </div>
                </div>

                <div className="col-md-6 text-center">
                  <label className="form-label fw-bold">Profile Picture</label>
                  <div className="d-flex flex-column align-items-center">
                    <div
                      style={{
                        width: 150,
                        height: 150,
                        borderRadius: "50%",
                        backgroundColor: "#d9d9d9",
                        backgroundImage: preview ? `url(${preview})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        marginBottom: "1rem",
                      }}
                    ></div>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control w-auto"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>

              {error && <div className="alert alert-danger mt-4">{error}</div>}
              {success && (
                <div className="alert alert-success mt-4">{success}</div>
              )}

              <div className="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary me-2 px-4"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading || !isChanged}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageProfile;

